/**
 * PublisherHeroWatch — the player half of the publisher hero.
 *
 * Watching is half of what a visitor came to do, so the right side of the hero
 * is a player rather than empty art: a feature video, the line under it, and a
 * strip of three more.
 *
 * Its own file because it owns state nothing else in the hero touches — which
 * video is featured, whether the embed has been mounted, the runtime the
 * <video> element reported — and because PublisherHero had grown past 900 lines
 * with this accounting for a third of it.
 *
 * NOT release-gated. A shipped title and a franchise have trailers and video
 * strips; the only thing a launch changes here is one word in the fallback
 * label ("Reveal Trailer" before, "Trailer" after).
 */

import React, {useState} from "react";
import {isSafeHref} from "./play/safeHref";
import {useGetDropRoster} from "@/hooks/drop/useGetDropRoster";
import {useGetDropYouTubeVideos} from "@/hooks/drop/useGetDropYouTubeVideos";

/** One assigned video in the hero strip. */
export interface DropVideoView {
    url: string;
    /** Publisher's label, or the resolved title where we could resolve one. */
    title?: string;
    durationSeconds?: number;
    thumbnailUrl?: string;
}

export interface PublisherHeroWatchProps {
    /** The feature video. Absent ⇒ this whole panel is not rendered. */
    trailerUrl?: string;
    /**
     * The strip under the feature — videos the publisher assigned.
     *
     * Assigned rather than derived because official highlights are not
     * necessarily on YouTube: a publisher may keep them on Twitch or Kick, or
     * want three specific ones rather than the three most recent. When this is
     * empty the strip falls back to the Drop's connected YouTube channel, so a
     * publisher who has curated nothing still gets a populated one.
     */
    videos?: DropVideoView[];
    /** Poster for the feature. The Drop's key art, unless YouTube has its own still. */
    trailerPosterUrl?: string;
    /** Drives the accessible names, and the label under the player. */
    gameTitle: string;
    /** Only reads `RELEASED` — the one word a launch changes down here. */
    releaseStatus?: "ANNOUNCED" | "BETA" | "RELEASED";
    /** The Drop, for the channel-video fallback and the co-streaming count. */
    followSlug?: string;
    /** Overrides the derived "N creators co-streaming right now" line. */
    watchNote?: string;
}

/**
 * Is this a video file we can play ourselves, rather than a page to link to?
 *
 * A reveal trailer is usually a file — on the publisher's CDN, or on ours when
 * they hand us the asset (how Full Throttle's hero video works today:
 * `heroSection.videoUrl` rendered through `<video><source type="video/mp4">`).
 * Both are the same case here. YouTube, Twitch and Kick are pages, not files,
 * and go through their own embeds or link out.
 *
 * Query strings are stripped before checking the extension — signed CDN URLs
 * routinely carry `?Expires=…&Signature=…` after the filename.
 */
export function isDirectMedia(url?: string): boolean {
    if (!url) return false;
    try {
        const path = new URL(url, window.location.origin).pathname.toLowerCase();
        return (/\.(mp4|webm|mov|m4v)$/).test(path);
    } catch {
        return false;
    }
}

/**
 * YouTube's own still for a video URL, or undefined if it isn't YouTube.
 *
 * `hqdefault` rather than `maxresdefault`: the latter 404s for anything never
 * encoded at 720p+, and a 404 renders as the broken tile this replaces.
 */
export function youTubeThumb(url?: string): string | undefined {
    const id = youTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : undefined;
}

/**
 * The YouTube video id in a watch/share/embed URL, or undefined.
 *
 * A trailer is almost always a YouTube link, and linking out to it hands the
 * viewer — and the session — to YouTube at the exact moment they engaged. We
 * can embed it, so we embed it.
 */
export function youTubeId(url?: string): string | undefined {
    if (!url) return undefined;
    try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, "");
        if (host === "youtu.be") return u.pathname.slice(1) || undefined;
        if (host.endsWith("youtube.com")) {
            if (u.pathname === "/watch") return u.searchParams.get("v") ?? undefined;
            const m = (/^\/(embed|v|shorts)\/([^/?#]+)/).exec(u.pathname);
            if (m) return m[2];
        }
        return undefined;
    } catch {
        return undefined;
    }
}

/** One tile in the strip, whichever source it came from. */
interface StripItem {
    key: string;
    href: string;
    title: string;
    durationSeconds?: number;
    thumbnailUrl?: string;
}

/** 161 → "2:41". Hours only appear when there are hours. */
function formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = Math.floor(seconds % 60);
    return hours > 0
        ? `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
        : `${minutes}:${String(secs).padStart(2, "0")}`;
}

export function PublisherHeroWatch({
    trailerUrl, videos, trailerPosterUrl, gameTitle, releaseStatus, followSlug, watchNote,
}: PublisherHeroWatchProps): React.ReactElement | null {
    /*
     * Validated before anything reads it. See play/safeHref.ts — and note the
     * declaration order: `featuredUrl` below falls back to this, and a `const`
     * referenced above its declaration is a temporal-dead-zone crash, not a
     * hoisted undefined.
     */
    const trailerHref = isSafeHref(trailerUrl) ? trailerUrl : undefined;

    /*
     * The strip. Assigned videos win; the Drop's YouTube channel is the
     * fallback so a publisher who has curated nothing still gets one. Both are
     * capped at three — the anchor's row, and the point at which the strip
     * starts competing with the feature above it.
     */
    const {data: channelVideos} = useGetDropYouTubeVideos(followSlug);
    const assigned = (videos ?? []).filter(v => isSafeHref(v.url));
    const strip: StripItem[] = assigned.length > 0
        ? assigned.slice(0, 3).map(v => ({
            key: v.url,
            href: v.url,
            title: v.title ?? "Watch",
            durationSeconds: v.durationSeconds,
            thumbnailUrl: v.thumbnailUrl,
        }))
        /*
         * No cast. `DropYouTubeVideoResponse` already declares videoId, title,
         * thumbnailUrl and durationSeconds — the local shape that used to sit
         * here restated the generated type, so a schema change would have
         * compiled clean and broken at runtime.
         */
        : (channelVideos?.videos ?? []).slice(0, 3).map(v => ({
            key: v.videoId,
            href: `https://www.youtube.com/watch?v=${v.videoId}`,
            title: v.title,
            durationSeconds: v.durationSeconds,
            thumbnailUrl: v.thumbnailUrl,
        }));

    /*
     * Which video the feature is showing. Starts on the trailer; clicking a
     * strip tile swaps it in rather than sending the viewer to another site.
     */
    const [featured, setFeatured] = useState<string | undefined>(trailerUrl);
    const featuredUrl = isSafeHref(featured) ? featured : trailerHref;

    /*
     * A reveal trailer is usually a file on the publisher's own CDN, not an
     * embed — so play it here with <video> instead of linking out. The runtime
     * then costs nothing: the element reports it on loadedmetadata, which is
     * why a CDN-hosted feature gets a duration badge without anyone typing one
     * or us calling an API.
     */
    const featuredIsFile = isDirectMedia(featuredUrl);
    const [fileDuration, setFileDuration] = useState<number>();

    /*
     * Click-to-play for the embed. Not auto-embedded: an iframe that
     * autoplays on load is both a bandwidth cost for someone who never
     * pressed play and a third-party frame we would rather mount on intent.
     */
    const [embedPlaying, setEmbedPlaying] = useState(false);
    const featuredYouTubeId = youTubeId(featuredUrl);

    /*
     * The still under the play button.
     *
     * A YouTube video already has a frame chosen for it, so use that in
     * preference to the caller's poster — which is the Drop's key art, and put
     * the same image behind the hero AND inside the player, making the trailer
     * read as a still panel rather than a video. `hqdefault` rather than
     * `maxresdefault`: the latter 404s on any upload that was never encoded at
     * 720p+, and a 404 here is the black box this replaces.
     */
    const posterSrc = youTubeThumb(featuredUrl) ?? trailerPosterUrl;

    /*
     * The feature's own duration. Only known when the trailer is one of the
     * assigned videos or the channel feed — nothing stores it on the Drop, and
     * a badge showing the wrong runtime is worse than no badge.
     */
    const featureDuration = (() => {
        /* The element's own metadata is the most reliable source when we have it. */
        if (fileDuration) return formatDuration(fileDuration);
        const match = assigned.find(v => v.url === featuredUrl)?.durationSeconds ??
            strip.find(s => s.href === featuredUrl)?.durationSeconds;
        return match ? formatDuration(match) : undefined;
    })();

    /** The label under the player follows whichever video is featured. */
    const featureLabel = assigned.find(v => v.url === featuredUrl)?.title ??
        strip.find(s => s.href === featuredUrl)?.title;

    /*
     * The roster, for the line under the player. React Query dedupes this with
     * CreatorTeamSection's identical call, so reading it here costs nothing —
     * and the number is what turns a trailer into an event ("38 official
     * creators co-streaming the reveal right now"). Says nothing when nobody
     * is live rather than printing a zero.
     */
    const {data: roster} = useGetDropRoster(followSlug);
    const liveNow = roster?.liveOnGame ?? 0;
    const derivedWatchNote = liveNow > 0
        ? `${liveNow} official creator${liveNow === 1 ? "" : "s"} co-streaming right now`
        : undefined;
    const note = watchNote ?? derivedWatchNote;

    if (!featuredUrl) return null;

    return (
        <div className="pub-hero__watch">
            {/*
              * A file plays here; a page links out.
              *
              * `preload="metadata"` matches how the Polaris hero video is served
              * (cdn.devourplay.gg, Cloudflare in front) — it fetches the header
              * rather than the file, which is what makes the runtime badge free
              * and keeps a 200 MB trailer off the critical path for someone who
              * never presses play.
              */}
            {featuredIsFile
                ? <div className="pub-hero__player pub-hero__player--file">
                    <video
                        className="pub-hero__video"
                        src={featuredUrl}
                        poster={posterSrc}
                        controls
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={(e) => {
                            const d = e.currentTarget.duration;
                            if (Number.isFinite(d) && d > 0) setFileDuration(d);
                        }}
                    />
                </div>
                : featuredYouTubeId
                    ? embedPlaying
                        /*
                         * Plays HERE. A trailer is the moment the visitor
                         * engages, and sending them to youtube.com at exactly
                         * that moment hands the session to YouTube — the page
                         * they came to is now a tab behind a video.
                         */
                        ? <div className="pub-hero__player pub-hero__player--file">
                            <iframe
                                className="pub-hero__video"
                                src={`https://www.youtube.com/embed/${featuredYouTubeId}?autoplay=1&rel=0&modestbranding=1`}
                                title={featureLabel ?? gameTitle}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>
                        : <button
                            type="button"
                            className="pub-hero__player"
                            onClick={() => setEmbedPlaying(true)}
                            aria-label={`Play ${featureLabel ?? gameTitle}`}
                        >
                            {posterSrc && <img className="pub-hero__poster" src={posterSrc} alt="" />}
                            <span className="pub-hero__play" aria-hidden="true" />
                            {featureDuration && <span className="pub-hero__duration">{featureDuration}</span>}
                        </button>
                /* Neither a file nor YouTube — Twitch/Kick pages we cannot embed from here. */
                    /*
                     * Labelled like the embed branch beside it — its children
                     * are an alt="" image and an aria-hidden glyph, so without
                     * this the link has no accessible name at all.
                     */
                    : <a
                        className="pub-hero__player"
                        href={featuredUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Watch ${featureLabel ?? gameTitle}`}
                    >
                        {posterSrc && <img className="pub-hero__poster" src={posterSrc} alt="" />}
                        <span className="pub-hero__play" aria-hidden="true" />
                        {featureDuration && <span className="pub-hero__duration">{featureDuration}</span>}
                    </a>
            }
            <div className="pub-hero__watch-meta">
                <b>{featureLabel ?? (releaseStatus === "RELEASED" ? "Trailer" : "Reveal Trailer")}</b>
                {note && <span>{note}</span>}
            </div>

            {/*
              * More to watch, under the feature.
              *
              * Videos the publisher assigned; the Drop's connected YouTube
              * channel when they have assigned none, so the strip is populated
              * without anyone maintaining a list that goes stale on the second
              * upload.
              *
              * A file swaps into the player above rather than navigating — same
              * reasoning as the roster: we hold the video, so sending someone
              * elsewhere to watch it is a choice, and the wrong one. Pages
              * (YouTube, Twitch, Kick) still open in a new tab because we
              * cannot play those in place.
              */}
            {strip.length > 0 &&
                <div className="pub-hero__strip">
                    {strip.map(v => {
                        /* Anything we can play in place — file or YouTube — swaps the feature. */
                        const playsHere = isDirectMedia(v.href) || Boolean(youTubeId(v.href));
                        const Tile = playsHere ? "button" : "a";
                        return (
                            <Tile
                                key={v.key}
                                className={`pub-hero__clip${v.href === featuredUrl ? " pub-hero__clip--current" : ""}`}
                                {...(playsHere
                                    ? {type: "button" as const, onClick: () => {
                                        setFeatured(v.href); setFileDuration(undefined);
                                    }}
                                    : {href: v.href, target: "_blank", rel: "noopener noreferrer"})}
                            >
                                {/*
                                  * Fall back to YouTube's own still.
                                  *
                                  * `thumbnailUrl` is optional and an assigned
                                  * video almost never carries one — a publisher
                                  * pastes a URL, not a poster — so the strip
                                  * rendered as three empty outlines with the
                                  * word WATCH in them. Every YouTube video
                                  * already has a frame; use it, the same way the
                                  * feature player does.
                                  */}
                                {(v.thumbnailUrl ?? youTubeThumb(v.href)) &&
                                    <img
                                        className="pub-hero__clip-art"
                                        src={v.thumbnailUrl ?? youTubeThumb(v.href)}
                                        alt=""
                                        loading="lazy"
                                    />
                                }
                                {v.durationSeconds
                                    ? <span className="pub-hero__clip-dur">{formatDuration(v.durationSeconds)}</span>
                                    : null}
                                <span className="pub-hero__clip-label">{v.title}</span>
                            </Tile>
                        );
                    })}
                </div>
            }
        </div>
    );
}
